import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import TerrainIcon from '@mui/icons-material/Terrain';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { actions } from '../../../store/store';
import SkeletonWrapper from '../../../tools/loader/skeletonWrapper';
import InfiniteScrollSentinel from '../../../tools/loader/infiniteScrollSentinel';
import ConfirmDialog from '../../../tools/modal/confirmDialog';
import { useSidebarWidth } from '../../../tools/hooks/useSidebarWidth';
import SidebarResizer from '../../../tools/navs/sidebarResizer';
import MisDraftStrip, { MIS_DRAFT } from '../misDraftStrip';
import { branchDocDefaults } from '../docSettings';
import { useDocFormat } from '../docFormat';
import PackingListForm from './packingListForm';
import PalletLabelForm from './palletLabelForm';
import PalletLabelDetail from './palletLabelDetail';
import PalletPrintDialog from './palletPrintDialog';
import { Retract } from '../../../tools/scrollRetract';

const DRAFT_KINDS = [MIS_DRAFT.label.kind];
const PAGE = 40;
const NO_COUNTS = { all: 0, available: 0, onList: 0 };

// Where a pallet stands. One colour per state, on the card's rail, its pill, the filter and the record, so a state is read at a glance everywhere. The light theme
// takes darker shades: these are small type on a pale tint.
const stateOf = (row) => (!row.list ? 'available' : row.list.status === 'final' ? 'final' : 'onList');
export const stateColors = (isDark) => ({
  available: isDark ? '#81c784' : '#2e7d32',
  onList:    isDark ? '#ffb74d' : '#b26a00',
  final:     isDark ? '#64b5f6' : '#1565c0',
});

function Metric({ value, label, T }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.35 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: T.TEXT_PRI, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.62rem', color: T.TEXT_TER }}>{label}</Typography>
    </Box>
  );
}

// All · Available · On a list. A coloured dot, the name and how many: the buttons say what they hold before they are pressed, and the count follows the search.
function StateFilter({ value, counts, colors, onChange, touch, T }) {
  const { t } = useTranslation();
  const options = [
    { v: 'all', label: t('mis.allTab'), n: counts.all, color: T.TEXT_SEC },
    { v: 'available', label: t('mis.lblFilterAvailable'), n: counts.available, color: colors.available },
    { v: 'onList', label: t('mis.lblFilterOnList'), n: counts.onList, color: colors.onList },
  ];
  return (
    <Box role="group" aria-label={t('common.status')} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {options.map((o) => {
        const on = value === o.v;
        return (
          <ButtonBase key={o.v} onClick={() => onChange(o.v)} aria-pressed={on} data-testid={`lbl-state-${o.v}`}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, height: touch ? 40 : 30, px: 1.1, borderRadius: '9px', border: '1px solid',
              borderColor: on ? o.color : T.BD, bgcolor: on ? `${o.color}1f` : 'transparent', transition: 'background-color 0.12s, border-color 0.12s',
              '&:hover': { bgcolor: on ? `${o.color}1f` : T.CTRL_BG } }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: o.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: touch ? '0.76rem' : '0.72rem', fontWeight: on ? 700 : 500, color: on ? T.TEXT_PRI : T.TEXT_SEC, whiteSpace: 'nowrap' }}>{o.label}</Typography>
            <Box data-testid={`lbl-count-${o.v}`} sx={{ minWidth: 20, height: 18, px: 0.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px',
              bgcolor: on ? `${o.color}2e` : T.CTRL_BG, fontSize: '0.64rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: on ? T.TEXT_PRI : T.TEXT_SEC }}>
              {o.n}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// One pallet of the list. The body opens its record; the box ticks it for printing with others, or for a packing list. Memoized, and every prop it
// is handed is stable (the handlers, the colours, the theme tokens, the formatter), so a tick, a keystroke in the search or opening a record redraws only
// the cards that changed - on 200 cards that was every card, 0.3 s a tick on an ordinary PC and 1.8 s on a slow one.
const PalletRecordCard = memo(function PalletRecordCard({ row, selected, ticked, onTick, onOpen, calendar, fmt, touch, colors, T }) {
  const { t } = useTranslation();
  const items = row.items || [];
  const pcs = items.reduce((a, it) => a + (Number(it.pcs) || 0), 0);
  const sqm = items.reduce((a, it) => a + (Number(it.sqm) || 0), 0);
  const codes = [...new Set(items.map((it) => it.code).filter(Boolean))];
  const state = stateOf(row);
  const color = colors[state];
  const stateLabel = state === 'available' ? t('mis.lblAvailable') : t(state === 'final' ? 'mis.lblFinalChip' : 'mis.lblOnListChip', { list: row.list.docNumber });
  const stones = [codes.slice(0, 3).join(', ') + (codes.length > 3 ? ` +${codes.length - 3}` : ''), row.processingType].filter(Boolean).join(' · ');
  const place = (row.list && row.list.destinationAddress) || row.destinationAddress;
  const line = { display: 'flex', alignItems: 'center', gap: 0.3, minWidth: 0 };
  return (
    <Box data-zebra-row="" data-pallet-row={row.reference} data-pallet-state={state} onClick={() => onOpen(row)}
      sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.9, pl: 1.25, pr: 1.25, borderRadius: '10px', cursor: 'pointer', minWidth: 0,
        border: '1px solid', borderColor: selected ? T.BD2 : 'transparent', bgcolor: selected ? T.CTRL_BG : 'transparent',
        transition: 'background-color 0.12s, border-color 0.12s', '&:hover': { bgcolor: T.CTRL_BG },
        '&::before': { content: '""', position: 'absolute', insetInlineStart: 0, top: 10, bottom: 10, width: '3px', borderRadius: '3px', bgcolor: color } }}>
      <Checkbox size="small" checked={ticked} onClick={(e) => e.stopPropagation()} onChange={() => onTick(row)}
        inputProps={{ 'aria-label': row.reference, 'data-testid': `lbl-tick-${row.reference}` }} sx={touch ? { width: 44, height: 44, mt: -0.5 } : { p: 0.5, mt: -0.25 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, minWidth: 0 }}>
          <Typography dir="ltr" sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: T.TEXT_PRI, flexShrink: 0 }}>{row.reference}</Typography>
          {row.palletId && <Box sx={{ px: 0.6, py: '1px', borderRadius: '4px', bgcolor: T.CTRL_BG, fontSize: '0.62rem', fontWeight: 700, color: T.TEXT_SEC, flexShrink: 0 }}>{row.palletId}</Box>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.6, py: '1px', borderRadius: '5px', bgcolor: `${color}1f`, minWidth: 0 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography noWrap data-testid="lbl-state" sx={{ fontSize: '0.6rem', fontWeight: 700, color }}>{stateLabel}</Typography>
          </Box>
          <Typography sx={{ marginInlineStart: 'auto', fontSize: '0.66rem', color: T.TEXT_TER, flexShrink: 0 }}>{fmt.date(row.insertDate, calendar)}</Typography>
        </Box>
        {stones && (
          <Box sx={{ ...line, mb: 0.35 }}>
            <TerrainIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{stones}</Typography>
          </Box>
        )}
        {(row.palletizedBy || place || row.labelForm) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.35, minWidth: 0 }}>
            {row.labelForm && (
              <Box data-testid="lbl-card-form" sx={{ ...line, flexShrink: 0 }}>
                <LibraryBooksOutlinedIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{t('mis.lfChip', { number: row.labelForm.docNumber })}</Typography>
              </Box>
            )}
            {row.palletizedBy && (
              <Box sx={{ ...line, flexShrink: place ? 1 : 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{row.palletizedBy}</Typography>
              </Box>
            )}
            {place && (
              <Box sx={{ ...line, flex: 1 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{place}</Typography>
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Metric value={items.length} label={t('mis.plRowsShort')} T={T} />
          <Metric value={pcs} label={t('mis.plColPcs')} T={T} />
          <Metric value={sqm.toFixed(4)} label={t('mis.plColSqm')} T={T} />
        </Box>
      </Box>
    </Box>
  );
});

// Labels: every pallet of the branch, whether a packing list has it yet or not. The same master-detail shell as packing lists and invoices: a record opens beside the list
// as the label it will print, with its print options. A label is made here without any list, and a packing list is made from the pallets ticked here.
// switcher (optional)  the Label forms / Pallets switch of the Labels tab, drawn at the start of the toolbar
// onNew    (optional)  what New does: inside the Labels tab it makes a label form (labels are kept in batches), and the unsent drafts live there
export default function PalletLabelsSection({ openId, onOpenIdConsumed, switcher = null, onNew = null }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMob = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const history = useHistory();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { can } = usePermissions();
  const { activeBranchId, activeBranch } = useBranch();
  const fmt = useDocFormat();

  const canCreate = can('mis:pallet:create') || can('mis:packingList:create');
  const canEdit = can('mis:pallet:edit') || can('mis:packingList:edit');
  const canDelete = can('mis:pallet:delete') || can('mis:packingList:delete');
  const canPrint = can('mis:packingList:pdf') || can('mis:pallet:create') || can('mis:pallet:edit');
  const canList = can('mis:packingList:create');
  const calendar = branchDocDefaults(activeBranch).dateSystem;
  const colors = useMemo(() => stateColors(isDark), [isDark]);

  const T = useMemo(() => ({
    PANEL_BG: isDark ? '#0d0d0d' : theme.palette.background.paper,
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    ROW_ALT:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  }), [isDark, theme.palette.background.paper, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(NO_COUNTS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [listResizing, setListResizing] = useState(false);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [ticked, setTicked] = useState(() => new Map());     // id -> code: the ticks survive a search and more pages
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftRefresh, setDraftRefresh] = useState(0);
  const [printIds, setPrintIds] = useState(null);
  const [listPreset, setListPreset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notice, setNotice] = useState('');

  const { width: listWidth, setWidth: setListWidth, resetWidth: resetListWidth } = useSidebarWidth('misPalletList', 420, { min: 320, max: 640 });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async (skip = 0) => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const res = await authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets`,
        params: { branchId: activeBranchId, limit: PAGE, skip, ...(state !== 'all' ? { state } : {}), ...(debounced.trim() ? { search: debounced.trim() } : {}) },
      });
      setRows((cur) => (skip ? [...cur, ...res.data.data] : res.data.data));
      setTotal(res.data.total);
      setCounts(res.data.counts || NO_COUNTS);
    } catch (_) {
      dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lblLoadFailed'), type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, authCtx, axiosGlobal, state, debounced, dispatch, t]);

  useEffect(() => { load(0); }, [load]);
  // a branch's ticks and open record mean nothing in another branch
  useEffect(() => { setTicked(new Map()); setSelectedId(null); setMobileDetail(false); }, [activeBranchId]);

  // a copied link: /mis?openPallet=<id>
  useEffect(() => {
    if (!openId) return;
    setSelectedId(openId);
    if (isMob) setMobileDetail(true);
    if (onOpenIdConsumed) onOpenIdConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const tick = useCallback((row) => setTicked((cur) => {
    const next = new Map(cur);
    if (next.has(row._id)) next.delete(row._id); else next.set(row._id, row.reference);
    return next;
  }), []);
  const tickAll = () => setTicked((cur) => {
    const next = new Map(cur);
    const all = rows.every((row) => next.has(row._id));
    rows.forEach((row) => { if (all) next.delete(row._id); else next.set(row._id, row.reference); });
    return next;
  });
  const open = useCallback((row) => { setSelectedId(row._id); if (isMob) setMobileDetail(true); }, [isMob]);
  const back = () => { setSelectedId(null); setMobileDetail(false); };
  const ids = useMemo(() => [...ticked.keys()], [ticked]);
  const selectedRow = useMemo(() => rows.find((row) => row._id === selectedId) || null, [rows, selectedId]);
  const allTicked = rows.length > 0 && rows.every((row) => ticked.has(row._id));

  const closeForm = (result) => {
    const wasEditing = Boolean(editing);
    setFormOpen(false); setEditing(null); setDraft(null);
    setDraftRefresh((n) => n + 1);
    if (!result) return;
    load(0);
    setDetailRefresh((n) => n + 1);
    if (wasEditing) {
      if (result.print) setPrintIds(result.saved.map((p) => p._id));
      return;
    }
    // what was just saved is ticked, so printing it or putting it on a list is one press away
    const next = new Map(result.saved.map((p) => [p._id, p.reference]));
    setTicked(next);
    if (result.print) setPrintIds([...next.keys()]);
  };

  // The ticked pallets as they are NOW, read again from the server: somebody may have put one on a list since it was ticked.
  const startList = async () => {
    setNotice('');
    try {
      const res = await authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/lookup`,
        params: { branchId: activeBranchId, code: [...ticked.values()].join(', ') },
      });
      const found = (res.data && res.data.rows) || [];
      const busy = found.filter((r) => r.status === 'on_list');
      if (busy.length) {
        setNotice(busy.map((r) => t('mis.lblOnOtherList', { code: r.pallet.reference, list: r.list.docNumber })).join(' '));
        return;
      }
      setListPreset({ pallets: found.filter((r) => r.status === 'found').map((r) => r.pallet) });
    } catch (_) {
      setNotice(t('mis.lblLookupFailed'));
    }
  };

  const remove = async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    try {
      await authCtx.jwtInst({ method: 'delete', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/${row._id}` });
      setTicked((cur) => { const next = new Map(cur); next.delete(row._id); return next; });
      if (selectedId === row._id) back();
      load(0);
    } catch (err) {
      const data = (err && err.response && err.response.data) || {};
      setNotice(data.code === 'PALLET_ON_LIST' ? t('mis.lblDeleteOnList', { code: data.pallet, list: data.docNumber }) : t('mis.lblDeleteFailed'));
      load(0);
    }
  };


  return (
    <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>

      {/* ── List panel ── */}
      {(!isMob || !mobileDetail) && (
        <Box sx={{ width: isMob ? '100%' : (selectedId ? listWidth : '100%'), flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: (!isMob && selectedId) ? `1px solid ${T.BD}` : 'none', bgcolor: T.PANEL_BG, overflow: 'hidden', position: 'relative',
          transition: listResizing ? 'none' : 'width 0.2s' }}>
          {!isMob && selectedId && (
            <SidebarResizer width={listWidth} side="right" onResize={(w) => { setListResizing(true); setListWidth(w); }} onDoubleClick={resetListWidth} />
          )}

          {/* search, New, the state filter: they fold away with the MIS top bar while the list scrolls down */}
          <Retract>
          <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
            {switcher}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, minWidth: 140, bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}`, borderRadius: '9px', px: 1, height: isMob ? 40 : 30 }}>
              <SearchIcon sx={{ fontSize: 15, color: T.TEXT_TER }} />
              <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('mis.lblSearchPlaceholder')}
                inputProps={{ 'aria-label': t('mis.lblSearchPlaceholder'), 'data-testid': 'lbl-search' }}
                sx={{ fontSize: isMob ? '16px' : '0.78rem', color: T.TEXT_PRI, flex: 1, '& input::placeholder': { color: T.TEXT_TER, opacity: 1 } }} />
              {search && (
                <IconButton size="small" onClick={() => setSearch('')} aria-label={t('common.close')} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 13, color: T.TEXT_TER }} />
                </IconButton>
              )}
            </Box>
            {canCreate && (
              <Button variant="contained" size="small" onClick={() => (onNew ? onNew() : (setEditing(null), setDraft(null), setFormOpen(true)))} data-testid="lbl-new"
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{ height: isMob ? 40 : 30, borderRadius: '8px', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, flexShrink: 0 }}>
                {onNew ? t('mis.lfNewTitle') : t('mis.lblNewTitle')}
              </Button>
            )}
          </Box>

          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
            <StateFilter value={state} counts={counts} colors={colors} onChange={setState} touch={isMob} T={T} />
            {canPrint && rows.length > 0 && (
              <Button size="small" onClick={tickAll} data-testid="lbl-select-all"
                sx={{ marginInlineStart: 'auto', fontSize: '0.68rem', textTransform: 'none', minWidth: 0, color: T.TEXT_SEC, minHeight: isMob ? 40 : 0 }}>
                {allTicked ? t('mis.lblClearSelection') : t('mis.lblSelectAll')}
              </Button>
            )}
          </Box>
          </Retract>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 0.75 }}>
            {canCreate && !onNew && (
              <MisDraftStrip kinds={DRAFT_KINDS} branchId={activeBranchId} T={T} refreshKey={draftRefresh}
                onOpen={(d) => { setEditing(null); setDraft(d); setFormOpen(true); }} />
            )}
            <SkeletonWrapper loading={loading && rows.length === 0} variant="table" count={6}>
              {rows.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, px: 3, gap: 1 }}>
                  <LabelOutlinedIcon sx={{ fontSize: 30, color: T.TEXT_TER }} />
                  <Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC, textAlign: 'center' }}>
                    {search || state !== 'all' ? t('mis.lblNoneMatch') : t('mis.lblNoneYet')}
                  </Typography>
                </Box>
              ) : (
                // With no record open the list has the whole width: cards side by side rather than one line a screen long.
                <Box sx={{ px: 1, py: 0.5, display: 'grid', gap: 0.75, gridTemplateColumns: !isMob && !selectedId ? 'repeat(auto-fill, minmax(380px, 1fr))' : 'minmax(0, 1fr)' }}>
                  {rows.map((row) => (
                    <PalletRecordCard key={row._id} row={row} selected={row._id === selectedId} ticked={ticked.has(row._id)} onTick={tick}
                      onOpen={open} calendar={calendar} fmt={fmt} touch={isMob} colors={colors} T={T} />
                  ))}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <InfiniteScrollSentinel onIntersect={() => load(rows.length)} hasMore={rows.length < total} loading={loading} />
                  </Box>
                </Box>
              )}
            </SkeletonWrapper>
          </Box>

          {notice && (
            <Box role="alert" data-testid="lbl-notice" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'error.main', flex: 1 }}>{notice}</Typography>
              <IconButton size="small" onClick={() => setNotice('')} aria-label={t('common.close')}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
            </Box>
          )}

          {ticked.size > 0 && (
            <Box data-testid="lbl-select-bar" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 1.5, pt: 1,
              pb: { xs: 'max(8px, env(safe-area-inset-bottom))', sm: 1 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
              <Typography data-testid="lbl-selected" sx={{ fontSize: '0.74rem', fontWeight: 700, color: T.TEXT_PRI }}>{t('mis.lblSelected', { count: ticked.size })}</Typography>
              <Button size="small" onClick={() => setTicked(new Map())} data-testid="lbl-clear" sx={{ textTransform: 'none', fontSize: '0.7rem', minHeight: { xs: 44, sm: 0 } }}>{t('mis.lblClearSelection')}</Button>
              <Box sx={{ marginInlineStart: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {canList && (
                  <Button size="small" variant="outlined" onClick={startList} data-testid="lbl-make-list" startIcon={<LocalShippingIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', minHeight: { xs: 44, sm: 0 } }}>{t('mis.lblCreateList')}</Button>
                )}
                {canPrint && (
                  <Button size="small" variant="contained" onClick={() => setPrintIds(ids)} data-testid="lbl-print" startIcon={<PrintIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', minHeight: { xs: 44, sm: 0 } }}>{t('mis.lblPrintButton')}</Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ── Record: the label as it will print, and its options ── */}
      {selectedId && (!isMob || mobileDetail) && (
        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', bgcolor: T.PANEL_BG }}>
          <PalletLabelDetail palletId={selectedId} initialRow={selectedRow} branchId={activeBranchId} refreshKey={detailRefresh} canPrint={canPrint} canEdit={canEdit} canDelete={canDelete}
            colors={colors} calendar={calendar} onBack={back} onEdit={(row) => { setEditing(row); setDraft(null); setFormOpen(true); }}
            onDelete={setConfirmDelete} onOpenList={(id) => history.push(`/mis?openPackingList=${id}`)} T={T} />
        </Box>
      )}

      {(canCreate || canEdit) && <PalletLabelForm open={formOpen} pallet={editing} draft={draft} onClose={closeForm} />}
      <PalletPrintDialog open={Boolean(printIds)} ids={printIds || []} branchId={activeBranchId} onClose={() => setPrintIds(null)} />
      {canList && (
        <PackingListForm open={Boolean(listPreset)} preset={listPreset}
          onClose={() => { setListPreset(null); setTicked(new Map()); load(0); setDetailRefresh((n) => n + 1); }} />
      )}
      <ConfirmDialog open={Boolean(confirmDelete)} destructive title={t('mis.lblDeleteTitle')}
        message={confirmDelete ? t('mis.lblDeleteBody', { code: confirmDelete.reference }) : ''}
        confirmLabel={t('common.delete')} cancelLabel={t('common.cancel')} onConfirm={remove} onClose={() => setConfirmDelete(null)} />
    </Box>
  );
}
